<template>
  <div :class="['task', { 'task--expanded': isExpanded }]">
    <div class="task__main">
      <HdCheckbox
        ref="checkbox"
        :value="isCompleted"
        name="task"
        class="task__checkbox"
        @input="handleTaskCompletion"
      />

      <p
        :class="['task__title', { 'task__title--urgent': isTimeCritical }]"
        @click.prevent="toggleDetailsVisibility"
        v-text="title"
      />

      <div class="task__quick-actions">
        <div>
          <p v-if="timeUntilDue" class="task__due" v-text="timeUntilDue" />
          <p v-if="timeSinceOverdue" class="task__overdue" v-text="timeSinceOverdue" />
        </div>
        <div class="task__actions-wrapper">
          <button
            v-show="!isExpanded"
            type="button"
            @click.prevent="togglePostponeOptionsVisibility"
          >
            <HdIcon :src="icons.postpone" class="task__postpone-icon" height="20" width="20" />
          </button>
          <ul v-if="arePostponeOptionsVisible" class="task__postpone-options">
            <li @click.prevent="togglePostponeOptionsVisibility">
              <HdIcon :src="icons.close" height="20" width="20" />
            </li>
            <li
              v-for="option in postponeTaskOptions"
              v-show="option.show"
              :key="option.value"
              class="task__postpone-option"
              @click.prevent="handleTaskPostponement(option.value)"
              v-html="option.label"
            />
          </ul>

          <button
            v-show="!isExpanded"
            type="button"
            @click.prevent="actionHandler(ACTIONS.EDIT_TASK)"
          >
            <HdIcon :src="icons.edit" class="task__edit-icon" height="20" width="20" />
          </button>

          <button type="button" @click.prevent="toggleDetailsVisibility">
            <HdIcon :src="icons.chevron" class="task__state-icon" height="20" width="20" />
          </button>
        </div>
      </div>
    </div>
    <div v-show="isExpanded" class="task__details">
      <p v-text="createdAt" />
      <br />
      <p v-text="description" />
      <br />
      <div class="task__actions">
        <button
          class="task__action task__action--secondary"
          @click="togglePostponeOptionsVisibility"
        >
          {{ $t('DASHBOARD.CARD.EXPANDED.TASKS.ACTIONS.POSTPONE') }}
        </button>
        <button class="task__action" @click="actionHandler(ACTIONS.EDIT_TASK)">
          {{ $t('DASHBOARD.CARD.EXPANDED.TASKS.ACTIONS.EDIT') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { HdIcon, HdCheckbox } from 'homeday-blocks';
import { chevron, edit, postpone, close } from 'homeday-assets';
import { POSTPONE_TASK_OPTIONS } from '@/config/TRANSLATION_CONSTANTS';
import { postponeTask, completeSimpleTask } from '@/services/apiMapper';
import { ACTION, MUTATION } from '@/store/CONSTANTS';
import dashboardCardMixin from '@/mixins/dashboardCardMixin';

export default {
  name: 'TaskCard',
  components: { HdIcon, HdCheckbox },
  mixins: [dashboardCardMixin],
  props: {
    deal: {
      type: Object,
      required: true,
    },
    cardDynamicData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isCompleted: false,
      isExpanded: false,
      arePostponeOptionsVisible: false,
    };
  },
  computed: {
    icons() {
      return {
        chevron,
        edit,
        postpone,
        close,
      };
    },
    title() {
      return this.cardDynamicData.title;
    },
    description() {
      return this.cardDynamicData.description;
    },
    isSmart() {
      return !!this.cardDynamicData.smart;
    },
    isTimeCritical() {
      return this.cardDynamicData.time_critical;
    },
    createdAt() {
      const formattedDate = new Date(this.cardDynamicData.created_at);

      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };

      const [date, time] = new Intl.DateTimeFormat('de-DE', options)
        .format(formattedDate)
        .split(',');

      return this.$t('DASHBOARD.CARD.EXPANDED.TASKS.CREATED_AT', {
        date,
        time,
      });
    },
    timeUntilDue() {
      const timeUntilDue = this.cardDynamicData.due_at;

      if (!timeUntilDue) {
        return null;
      }

      const dueDate = new Date(timeUntilDue);
      const currentDate = new Date();
      const remainingDays = Number(Math.ceil((dueDate - currentDate) / (1000 * 60 * 60 * 24)));

      if (remainingDays <= 0) {
        return null;
      }

      return this.$t('DASHBOARD.CARD.EXPANDED.TASKS.DAYS_UNTIL_DUE', {
        value: remainingDays,
      });
    },
    timeSinceOverdue() {
      const timeSinceOverdue = this.cardDynamicData.due_at;

      if (!timeSinceOverdue) {
        return null;
      }

      const overdueDate = new Date(timeSinceOverdue);
      const currentDate = new Date();
      const DaysSinceOverdue = Number(
        Math.ceil((currentDate - overdueDate) / (1000 * 60 * 60 * 24)),
      );
      const monthsSinceOverdue = Math.floor(DaysSinceOverdue / 30);
      const yearsSinceOverdue = Math.floor(monthsSinceOverdue / 12);

      if (DaysSinceOverdue <= 0) {
        return null;
      }
      if (yearsSinceOverdue > 0) {
        return this.$t('DASHBOARD.CARD.EXPANDED.TASKS.YEARS_SINCE_OVERDUE', {
          value: yearsSinceOverdue,
        });
      }
      if (monthsSinceOverdue > 0) {
        return this.$t('DASHBOARD.CARD.EXPANDED.TASKS.MONTHS_SINCE_OVERDUE', {
          value: monthsSinceOverdue,
        });
      }

      return this.$t('DASHBOARD.CARD.EXPANDED.TASKS.DAYS_SINCE_OVERDUE', {
        value: DaysSinceOverdue,
      });
    },
    postponeTaskOptions() {
      return Object.keys(POSTPONE_TASK_OPTIONS).map((key) => {
        const value = key.toLowerCase();
        const currentDateTime = new Date();

        return {
          value,
          label: POSTPONE_TASK_OPTIONS[key],
          show: value === 'this_evening' ? currentDateTime.getHours() < 17 : true,
        };
      });
    },
  },
  methods: {
    toggleDetailsVisibility() {
      this.isExpanded = !this.isExpanded;
    },
    togglePostponeOptionsVisibility() {
      this.arePostponeOptionsVisible = !this.arePostponeOptionsVisible;
    },
    notificationHandler(success, message) {
      this.$store.commit(MUTATION.DASHBOARD.SET_NOTIFICATION, {
        message,
        type: success ? 'success' : 'error',
        autoClose: true,
      });
    },
    async handleTaskPostponement(postponeTo) {
      const { success, message } = await postponeTask({
        dealUid: this.deal.uid,
        taskUid: this.cardDynamicData.uid,
        postponeTo,
      });

      this.togglePostponeOptionsVisibility();
      this.notificationHandler(success, message);

      this.$store.dispatch(ACTION.DASHBOARD.FETCH_EXPANDED_DEAL, { dealUid: this.deal.uid });

      return success;
    },
    async handleTaskCompletion() {
      if (this.isSmart) {
        // Smart tasks can only be completed in Connect
        return this.actionHandler(this.ACTIONS.COMPLETE_TASK);
      }

      // Simple tasks can be completed here
      const { success, message } = await completeSimpleTask({
        dealUid: this.deal.uid,
        taskUid: this.cardDynamicData.uid,
      });

      this.notificationHandler(success, message);

      this.$store.dispatch(ACTION.DASHBOARD.FETCH_EXPANDED_DEAL, { dealUid: this.deal.uid });
      return success;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.task {
  $root: &;
  cursor: pointer;
  position: relative;

  &__main {
    display: flex;
    align-items: center;
  }

  &__checkbox {
    margin-bottom: 0 !important;
    margin-right: $sp-s + $sp-xs;
    align-self: flex-start;
    margin-top: $sp-xxs;

    ::v-deep .field__helper {
      display: none;
    }
  }

  &__title {
    @include fontGroup(100, 600);
    color: $primary-color;
    margin-right: $sp-s;
    align-self: flex-start;

    &--urgent {
      color: $error-color;
    }
  }

  &__quick-actions {
    flex-shrink: 0;
    margin-left: auto;

    #{$root}__actions-wrapper {
      display: flex;
      justify-content: flex-end;
    }

    button {
      padding: 0;
      border: none;
      outline: none;
      background: transparent;
    }
  }

  &__due,
  &__overdue {
    @include font('DS-80');
    color: getShade($quaternary-color, 80);
    text-align: right;
    line-height: $sp-m + $sp-xxs;
    margin-top: $sp-xs;
    margin-bottom: $sp-xs;
  }

  &__overdue {
    color: getShade($monza-red, 80);
  }

  &__details {
    margin-top: $sp-s;
    margin-left: $sp-l;

    p {
      @include fontGroup(90, 400);

      &:first-of-type {
        color: getShade($quaternary-color, 80);
      }
    }
  }

  &__postpone-icon,
  &__edit-icon,
  &__state-icon {
    cursor: pointer;
  }

  &__postpone-icon {
    margin-right: $sp-s;
  }

  &__edit-icon {
    margin-right: $sp-s;
  }

  &__state-icon {
    transform: rotate(90deg) scale(1.5);
    transition: transform $time-s ease-in-out;

    @at-root .task--expanded & {
      transform: rotate(-90deg) scale(1.5);
    }
  }

  &__postpone-options {
    position: absolute;
    top: $sp-l - $sp-xs;
    left: $sp-l;
    background-color: $primary-bg;
    border-radius: $sp-s;
    box-shadow: 0 0 24px rgba(26, 27, 60, 0.16);
    width: 258px;
    padding: ($sp-m + $sp-s) $sp-l $sp-m $sp-m;
    z-index: 31;

    li:first-child {
      position: absolute;
      right: 0;
      top: 0;
      padding: $sp-s;
    }
  }

  &__postpone-option {
    @include fontGroup(90, 400);

    &:hover {
      cursor: pointer;
      font-weight: 600;
    }

    &:not(:last-of-type) {
      margin-bottom: $sp-s;
    }

    ::v-deep span {
      @include fontGroup(80, 400);
    }
  }

  &__actions {
    display: flex;
  }

  &__action {
    $borderWidth: $sp-xxs;
    position: relative;
    border: none;
    text-align: left;
    border-radius: $sp-xxs;
    cursor: pointer;
    padding: $sp-s ($sp-m - $sp-xs);
    background-color: getShade($secondary-color, 120);
    @include fontGroup(90, 600);
    margin-right: $sp-s + $sp-xs;
    margin-top: $sp-s + $sp-xs;
    color: $white;
    height: $sp-l;
    border-width: $borderWidth;
    line-height: #{$sp-m - $borderWidth};

    &:hover {
      @include elevation(2);
    }

    &--secondary {
      border-style: solid;
      border-color: getShade($secondary-color, 120);
      color: getShade($secondary-color, 120);
      background-color: $white;
    }
  }
}
</style>
